import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import {
  CreateBusinessUnitSchema,
  UpdateBusinessUnitSchema,
  BusinessUnitSchema,
  BusinessUnitWithApplicationsSchema,
  UpdateApplicationBusinessUnitsSchema,
  ApplicationBusinessUnitWithDetailsSchema,
  ErrorResponseSchema,
  BusinessUnitDashboardMetricsSchema,
  BusinessUnitPerformanceMetricsSchema,
  BusinessUnitSummaryMetricsSchema,
  BusinessUnitRiskDistributionSchema,
  TopRiskyBusinessUnitSchema,
  BusinessUnitRemediationProgressSchema,
  BusinessUnitComplianceScoreSchema,
} from './business-units.schema';
import { BUSINESS_UNITS_PATH_NAME } from './business-units.constants';
import { z } from 'zod';

export const BusinessUnitsContract = terraContract
  .prefix(`/${BUSINESS_UNITS_PATH_NAME}`)
  .router({
    getAllBusinessUnits: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'Get all business units',
        description:
          'Retrieves all business units configured for the tenant with their associated applications.',
        inputStructure: 'detailed',
      })
      .output(BusinessUnitWithApplicationsSchema.array()),
    getBusinessUnit: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}',
        summary: 'Get a business unit by ID',
        description:
          'Retrieves details for a specific business unit including its applications.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            id: z.string(),
          }),
        }),
      )
      .output(BusinessUnitWithApplicationsSchema)
      .errors({ NOT_FOUND: { status: 404, data: ErrorResponseSchema } }),
    getApplicationBusinessUnits: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/applications/{applicationId}/business-units',
        summary: 'Get business units for an application',
        description:
          'Retrieves all business units associated with a specific application.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            applicationId: z.string(),
          }),
        }),
      )
      .output(ApplicationBusinessUnitWithDetailsSchema.array()),
    createBusinessUnit: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/',
        summary: 'Create a new business unit',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: CreateBusinessUnitSchema }))
      .output(BusinessUnitSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        CONFLICT: { status: 409, data: ErrorResponseSchema },
        UNPROCESSABLE_CONTENT: { status: 422, data: ErrorResponseSchema },
      }),
    updateBusinessUnit: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{id}',
        summary: 'Update a business unit',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            id: z.string(),
          }),
          body: UpdateBusinessUnitSchema,
        }),
      )
      .output(BusinessUnitSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        CONFLICT: { status: 409, data: ErrorResponseSchema },
        UNPROCESSABLE_CONTENT: { status: 422, data: ErrorResponseSchema },
      }),
    deleteBusinessUnit: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}',
        summary: 'Delete a business unit',
        inputStructure: 'detailed',
        successStatus: 204,
      })
      .input(
        z.object({
          params: z.object({
            id: z.string(),
          }),
        }),
      )
      .output(z.null())
      .errors({ NOT_FOUND: { status: 404, data: ErrorResponseSchema } }),
    updateApplicationBusinessUnits: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PATCH',
        path: '/applications/{applicationId}/business-units',
        summary: 'Update business units for an application',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            applicationId: z.string(),
          }),
          body: UpdateApplicationBusinessUnitsSchema,
        }),
      )
      .output(ApplicationBusinessUnitWithDetailsSchema.array()),
    getDashboardMetrics: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/dashboard',
        summary: 'Get comprehensive business unit metrics for dashboard',
        inputStructure: 'detailed',
      })
      .output(BusinessUnitDashboardMetricsSchema),
    getPerformanceMetrics: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/performance',
        summary: 'Get business unit performance metrics for charts',
        inputStructure: 'detailed',
      })
      .output(BusinessUnitPerformanceMetricsSchema.array()),
    getSummaryMetrics: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/summary',
        summary: 'Get business unit summary metrics',
        inputStructure: 'detailed',
      })
      .output(BusinessUnitSummaryMetricsSchema.array()),
    getBusinessUnitMetrics: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{id}/metrics',
        summary: 'Get metrics for a specific business unit',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: z.object({
            id: z.string(),
          }),
        }),
      )
      .output(BusinessUnitPerformanceMetricsSchema)
      .errors({ NOT_FOUND: { status: 404, data: ErrorResponseSchema } }),
    getRiskDistribution: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/risk-distribution',
        summary: 'Get business unit risk level distribution',
        inputStructure: 'detailed',
      })
      .output(BusinessUnitRiskDistributionSchema),
    getTopRiskyBusinessUnits: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/top-risky',
        summary: 'Get top risky business units',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          query: z
            .object({
              limit: z.string().optional().default('5'),
            })
            .optional(),
        }),
      )
      .output(TopRiskyBusinessUnitSchema.array()),
    getRemediationProgress: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/remediation-progress',
        summary: 'Get remediation progress for all business units',
        inputStructure: 'detailed',
      })
      .output(BusinessUnitRemediationProgressSchema.array()),
    getComplianceScores: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/metrics/compliance-scores',
        summary: 'Get compliance scores for all business units',
        inputStructure: 'detailed',
      })
      .output(BusinessUnitComplianceScoreSchema.array()),
  });
